// WithdrawPage — iOS HIG prop-firm withdrawal (replaces "Sell")
// Layout:
//   - Top nav: < Back | Withdraw
//   - Available balance hero (with profit split breakdown)
//   - Withdrawal amount input
//   - Method selector (Bank, Wire, USDT, Crypto)
//   - Summary card (split, fees, net)
//   - Request withdrawal button

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TIERS, DEMO_CHALLENGE } from '../../data/platforms';

const METHODS = [
  { key: 'bank',  label: 'Bank transfer', sub: 'ACH · 1-3 business days', icon: 'bank' },
  { key: 'wire',  label: 'Wire transfer', sub: 'International · 1-2 days',   icon: 'wire' },
  { key: 'usdt',  label: 'USDT (TRC-20)', sub: 'Crypto · ~10 minutes',        icon: 'crypto' },
  { key: 'btc',   label: 'Bitcoin',       sub: 'Crypto · ~30 minutes',        icon: 'btc' },
];

export function WithdrawPage() {
  const nav = useNavigate();
  const tier = TIERS[DEMO_CHALLENGE.tier];
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank');
  const num = parseFloat(amount.replace(/,/g, '')) || 0;
  const fee = method === 'wire' ? 25 : method === 'btc' ? 5 : 0;
  const net = num - fee;

  return (
    <div className="min-h-screen bg-white pb-[calc(96px+env(safe-area-inset-bottom))]">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Withdraw</div>
        <div className="w-9" />
      </div>

      {/* Available balance hero */}
      <div className="px-4 pt-4">
        <div className="rounded-[20px] bg-tf-text-1 p-5 text-white">
          <div className="text-[12px] text-white/60 uppercase tracking-wide">Available to withdraw</div>
          <div className="mt-1 text-[36px] font-bold tabular-nums tracking-tight">${(12480.20).toLocaleString()}</div>
          <div className="mt-1 text-[12px] text-white/60">From 1 active challenge · {tier.profitSplit}% split</div>
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-white/10 pt-3">
            <div>
              <div className="text-[11px] text-white/60">Your share</div>
              <div className="text-[14px] font-semibold tabular-nums">${(num * tier.profitSplit / 100).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/60">Funder Pro share</div>
              <div className="text-[14px] font-semibold tabular-nums">${(num * (100 - tier.profitSplit) / 100).toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Amount input */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Amount</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="flex items-center px-4 py-3">
          <span className="text-[15px] text-tf-text-3">Withdraw</span>
          <div className="ml-auto flex items-baseline gap-1">
            <span className="text-[15px] text-tf-text-3">$</span>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0.00"
              data-testid="withdraw-amount"
              className="w-32 text-right text-[20px] font-bold tabular-nums text-tf-text-1 outline-none"
            />
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-tf-border px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-tf-text-3">Max</span>
            <span className="text-[15px] font-semibold tabular-nums text-tf-text-1">$12,480.20</span>
          </div>
          <button
            onClick={() => setAmount('12480.20')}
            className="rounded-full bg-tf-surface-1 px-3 py-1 text-[12px] font-semibold text-tf-text-1 active:opacity-40"
          >
            Max
          </button>
        </div>
      </div>

      {/* Method */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Method</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {METHODS.map((m, i) => (
          <button
            key={m.key}
            onClick={() => setMethod(m.key)}
            data-testid={`method-${m.key}`}
            className={['flex w-full items-center justify-between border-tf-border px-4 py-3 text-left',
              i < METHODS.length - 1 ? 'border-b' : '', method === m.key ? 'bg-tf-blue/[0.05]' : ''].join(' ')}
          >
            <div>
              <div className="text-[15px] font-semibold text-tf-text-1">{m.label}</div>
              <div className="text-[12px] text-tf-text-3">{m.sub}</div>
            </div>
            <div className={['flex h-6 w-6 items-center justify-center rounded-full border-2',
              method === m.key ? 'border-[#007AFF] bg-tf-blue' : 'border-[#C7C7CC]'].join(' ')}>
              {method === m.key && <div className="h-2 w-2 rounded-full bg-white" />}
            </div>
          </button>
        ))}
      </div>

      {/* Summary */}
      {num > 0 && (
        <div className="mx-4 mt-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
          <Row label="Withdrawal" value={`$${num.toFixed(2)}`} />
          <Row label="Processing fee" value={`$${fee.toFixed(2)}`} />
          <Row label="You receive" value={`$${net.toFixed(2)}`} bold last />
        </div>
      )}

      {/* Action */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-tf-border bg-white/95 backdrop-blur-xl" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="mx-auto max-w-[640px] px-4 py-3 md:px-6">
          <button
            disabled={num === 0}
            data-testid="withdraw-submit"
            className="flex h-[52px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99] disabled:opacity-30"
          >
            Request withdrawal
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, last }: { label: string; value: string; bold?: boolean; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className={['text-[14px] text-tf-text-3', bold ? 'font-semibold text-tf-text-1' : ''].join(' ')}>{label}</span>
      <span className={['text-[14px] font-medium text-tf-text-1', bold ? 'text-[16px] font-bold' : ''].join(' ')}>{value}</span>
    </div>
  );
}
